import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Box, Typography, Snackbar, Alert, CircularProgress, TextField, InputAdornment, Button, Dialog, DialogActions, List, ListItem, ListItemText, ClickAwayListener, Paper, Chip, Divider, useMediaQuery, useTheme, } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaidIcon from '@mui/icons-material/Paid';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useAuth } from '../context/AuthContext';
import CenterLoader from './Test/components/CenterLoader';
import { APPBAR_HEIGHT } from '../utils/theme';


// ─── Searchable Field ─────────────────────────────────────────────────────────
// Palette matches the rest of the dashboard's modern theme (Home2.js /
// DashboardItems.js / UserDefineColumns.jsx / ConfigureModule.jsx):
// #1877F2 blue accent, #EEF1F6 hairline borders, soft rgba(15,23,42,...) shadows.

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        bgcolor: '#fff',
        fontSize: '0.78rem',
        height: 32,
        '& input': { padding: '0 10px' },
        '& fieldset': { borderColor: '#EEF1F6' },
        '&:hover fieldset': { borderColor: '#1877F2' },
        '&.Mui-focused fieldset': { borderColor: '#1877F2', borderWidth: 1.5 },
    },
};

const SearchableField = ({ value, onChange, placeholder, connectionString, companyId, bookType }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [options, setOptions]       = useState([]);
    const [open, setOpen]             = useState(false);
    const [busy, setBusy]             = useState(false);
    const [position, setPosition]     = useState({ top: 0, left: 0, width: 0 });
    const [isUserTyping, setIsUserTyping] = useState(false); // ✓ NEW: Track if user is actively typing
    const debounceRef                 = useRef(null);
    const selectingRef                = useRef(false);
    const inputRef                    = useRef(null);

    useEffect(() => { setInputValue(value || ''); }, [value]);

    // ✓ Calculate dropdown position
    const updatePosition = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        if (open) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [open]);

    useEffect(() => {
        if (selectingRef.current) { selectingRef.current = false; return; }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setBusy(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: 'FirmName', search: inputValue.trim() || '', companyId },
                });
                const data = Array.isArray(res.data.data) ? res.data.data : [];
                const list = data.map(i => ({
                    id: i.id || i.ClientId,
                    clientname: i.FirmName || i.companyName || '',
                    City: i.City || '',
                    ContactPerson: i.ContactPerson || '',
                    MobileNo: i.MobileNo || '',
                    EmailId: i.EmailId || '',
                    GSTNo: i.GSTNo || ''
                }));
                setOptions(list);
                // ✓ MODIFIED: Only show dropdown if user is actively typing
                setOpen(isUserTyping && list.length > 0 && inputValue.trim() !== '');
            } catch { setOptions([]); setOpen(false); }
            finally { setBusy(false); }
        }, 500);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [inputValue, connectionString, companyId, bookType, isUserTyping]);

    const handleChange = (e) => {
        setInputValue(e.target.value);
        onChange(e.target.value);
        setIsUserTyping(true); // ✓ NEW: User is actively typing
        selectingRef.current = false;
    };

    const handleSelect = (item) => {
        selectingRef.current = true;
        setInputValue(item.clientname || '');
        onChange({ id: item.id || '', name: item.clientname || '' });
        setOptions([]);
        setOpen(false);
        setIsUserTyping(false); // ✓ NEW: User finished selecting
    };

    const handleInputFocus = () => {
        updatePosition();
        setIsUserTyping(false); // ✓ NEW: Focus alone doesn't trigger typing
    };

    const handleInputBlur = () => {
        setIsUserTyping(false); // ✓ NEW: User left the field
        setOpen(false); // ✓ Close dropdown on blur
    };

    return (
        <>
            <Box ref={inputRef} sx={{ position: 'relative', width: '100%' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={inputValue}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    InputProps={{
                        endAdornment: busy ? <InputAdornment position="end"><CircularProgress size={12} sx={{ color: '#1565c0' }} /></InputAdornment> : null
                    }}
                    sx={inputSx}
                />
            </Box>

            {/* ✓ REDESIGNED: Portal-based dropdown to avoid z-index issues */}
            {open && options.length > 0 && ReactDOM.createPortal(
                <Box
                    sx={{
                        position: 'fixed',
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        width: `${position.width}px`,
                        zIndex: 99999,
                        maxHeight: '250px',
                        overflowY: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #EEF1F6', boxShadow: '0 16px 40px rgba(15,23,42,0.14)', overflow: 'hidden' }}>
                        <List dense disablePadding>
                            {options.map((item, idx) => (
                                <ListItem
                                    key={item.id || idx}
                                    button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(item)}
                                    sx={{
                                        py: 1,
                                        px: 1.5,
                                        cursor: 'pointer',
                                        borderBottom: idx < options.length - 1 ? '1px solid #F3F6FC' : 'none',
                                        '&:hover': { bgcolor: '#EBF3FF' },
                                        bgcolor: '#fff',
                                        transition: 'all 0.15s ease'
                                    }}>
                                    <ListItemText
                                        primary={item.clientname}
                                        secondary={
                                            <Box sx={{ display: 'flex', gap: 1, fontSize: '0.65rem', color: '#64748b', flexWrap: 'wrap', mt: 0.5 }}>
                                                {item.City && <span>📍 {item.City}</span>}
                                                {item.ContactPerson && <span>👤 {item.ContactPerson}</span>}
                                                {item.MobileNo && <span>📱 {item.MobileNo}</span>}
                                                {item.GSTNo && <span>🏢 {item.GSTNo}</span>}
                                            </Box>
                                        }
                                        primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500, color: '#1a2332' }}
                                        secondaryTypographyProps={{ fontSize: '0.65rem', color: '#64748b' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Box>,
                document.body
            )}

            {/* ✓ Close dropdown on outside click */}
            {open && <Box
                onClick={() => setOpen(false)}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9998
                }}
            />}
        </>
    );
};


// ─── Group Panel (Bank or Cash) ───────────────────────────────────────────────

const LABEL_W = 180; // px — fixed label column width on desktop
const ROW_MIN_W = 640; // px — min width for the label+receipt+payment grid before it scrolls instead of clipping

const GroupPanel = ({ title, subtitle, headerColor, headerBg, icon, rows, colA, colB, onChangeA, onChangeB, connectionString, companyId, isMobile, bookTypeA, bookTypeB }) => (
    <Box sx={{ border: '1px solid #EEF1F6', borderRadius: '14px', overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 6px 18px rgba(15,23,42,0.05)', flexShrink: 0 }}>

        {/* ── Panel header band ─────────────────────────────── */}
        <Box sx={{ background: headerBg, px: { xs: 1.5, md: 2.25 }, py: 1.3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.18)', color: headerColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: headerColor, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', color: headerColor, opacity: 0.75 }}>{subtitle}</Typography>
                </Box>
            </Box>
            <Chip label={`${rows.length} fields`} size="small"
                sx={{ height: 19, fontSize: '0.64rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: headerColor, border: `1px solid ${headerColor}33`, borderRadius: '7px', flexShrink: 0 }} />
        </Box>

        {/* ── Scrollable body: column headers + rows scroll together so nothing gets clipped ── */}
        <Box sx={{ overflowX: 'auto', overflowY: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
            <Box sx={{ minWidth: isMobile ? 'auto' : ROW_MIN_W }}>

                {/* Column headers (desktop only) */}
                {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_W}px 1fr 1fr`, bgcolor: '#F7FAFF', borderBottom: '1px solid #EEF1F6' }}>
                        <Box sx={{ px: 1.75, py: 0.85, borderRight: '1px solid #EEF1F6' }}>
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Account Head</Typography>
                        </Box>
                        <Box sx={{ px: 1.75, py: 0.85, borderRight: '1px solid #EEF1F6', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <ArrowDownwardIcon sx={{ fontSize: 11, color: '#1877F2' }} />
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#1877F2', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Receipt</Typography>
                        </Box>
                        <Box sx={{ px: 1.75, py: 0.85, display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <ArrowUpwardIcon sx={{ fontSize: 11, color: '#6d4c41' }} />
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#6d4c41', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Payment</Typography>
                        </Box>
                    </Box>
                )}

                {/* Field rows */}
                {rows.map((row, idx) => (
                    <Box key={row.key} sx={{
                        display: isMobile ? 'flex' : 'grid',
                        flexDirection: isMobile ? 'column' : undefined,
                        gridTemplateColumns: isMobile ? undefined : `${LABEL_W}px 1fr 1fr`,
                        bgcolor: idx % 2 === 0 ? '#fff' : '#F9FBFE',
                        borderBottom: idx < rows.length - 1 ? '1px solid #F3F6FC' : 'none',
                    }}>
                        {/* Label */}
                        <Box sx={{ px: 1.75, py: isMobile ? 0.85 : 1, borderRight: isMobile ? 'none' : '1px solid #F3F6FC', display: 'flex', alignItems: 'center', borderBottom: isMobile ? '1px solid #F3F6FC' : 'none' }}>
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0F172A' }}>{row.label}</Typography>
                        </Box>

                        {/* Receipt input */}
                        <Box sx={{ px: 1.25, py: 0.85, borderRight: isMobile ? 'none' : '1px solid #F3F6FC' }}>
                            {isMobile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.4 }}>
                                    <ArrowDownwardIcon sx={{ fontSize: 10, color: '#1877F2' }} />
                                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#1877F2', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Receipt</Typography>
                                </Box>
                            )}
                            <SearchableField value={colA[`${row.key}Name`]} onChange={(v) => onChangeA(row.key, v)} placeholder={row.placeholder} connectionString={connectionString} companyId={companyId} bookType={bookTypeA} />
                        </Box>

                        {/* Payment input */}
                        <Box sx={{ px: 1.25, py: 0.85 }}>
                            {isMobile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.4 }}>
                                    <ArrowUpwardIcon sx={{ fontSize: 10, color: '#6d4c41' }} />
                                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#6d4c41', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment</Typography>
                                </Box>
                            )}
                            <SearchableField value={colB[`${row.key}Name`]} onChange={(v) => onChangeB(row.key, v)} placeholder={row.placeholder} connectionString={connectionString} companyId={companyId} bookType={bookTypeB} />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);


// ─── Main Component ───────────────────────────────────────────────────────────

const BANK_ROWS = [
    { key: 'bankCommA', label: 'Bank Charges A/c', placeholder: 'e.g., BANK CHARGES' },
    { key: 'tdsA',      label: 'T.D.S. A/c',          placeholder: 'e.g., TDS A/C'      },
    { key: 'roundOffA', label: 'Round Off A/c',        placeholder: 'e.g., KASAR A/C'   },
];
const CASH_ROWS = [
    { key: 'tdsA',      label: 'T.D.S. A/c',    placeholder: 'e.g., TDS A/C'    },
    { key: 'roundOffA', label: 'Round Off A/c',  placeholder: 'e.g., KASAR A/C' },
];

export default function BankCashPosting({ connectionString }) {
    const { user, selectedCompany } = useAuth();
    const API       = `${connectionString}/api`;
    const userId    = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;

    const theme       = useTheme();
    const isMobile    = useMediaQuery(theme.breakpoints.down('md'));

    const [loading,     setLoading]     = useState(false);
    const [snackbar,    setSnackbar]    = useState({ open: false, message: '', severity: 'success' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [bankReceipt, setBankReceipt] = useState({ bankCommAId: null, bankCommAName: '', tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [bankPayment, setBankPayment] = useState({ bankCommAId: null, bankCommAName: '', tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [cashReceipt, setCashReceipt] = useState({ tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [cashPayment, setCashPayment] = useState({ tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await axios.get(`${API}/BankCashGet`, { params: { companyId, userId } });
            const data = res.data || {};
            setBankReceipt({ bankCommAId: data.bankReceiptBankCommAId || null, bankCommAName: data.bankReceiptBankCommAName || '', tdsAId: data.bankReceiptTdsAId || null, tdsAName: data.bankReceiptTdsAName || '', roundOffAId: data.bankReceiptRoundOffAId || null, roundOffAName: data.bankReceiptRoundOffAName || '' });
            setBankPayment({ bankCommAId: data.bankPaymentBankCommAId || null, bankCommAName: data.bankPaymentBankCommAName || '', tdsAId: data.bankPaymentTdsAId || null, tdsAName: data.bankPaymentTdsAName || '', roundOffAId: data.bankPaymentRoundOffAId || null, roundOffAName: data.bankPaymentRoundOffAName || '' });
            setCashReceipt({ tdsAId: data.cashReceiptTdsAId || null, tdsAName: data.cashReceiptTdsAName || '', roundOffAId: data.cashReceiptRoundOffAId || null, roundOffAName: data.cashReceiptRoundOffAName || '' });
            setCashPayment({ tdsAId: data.cashPaymentTdsAId || null, tdsAName: data.cashPaymentTdsAName || '', roundOffAId: data.cashPaymentRoundOffAId || null, roundOffAName: data.cashPaymentRoundOffAName || '' });
            // setSnackbar({ open: true, message: 'Configuration loaded.', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Failed to load data.', severity: 'error' });
        } finally { setLoading(false); }
    }, [API, companyId, userId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Save ───────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            await axios.post(`${API}/BankCashSave`, {
                companyId, userId, GCompId: selectedCompany?.GCompId || null,
                bankReceiptBankCommAId: bankReceipt.bankCommAId, bankReceiptBankCommAName: bankReceipt.bankCommAName,
                bankReceiptTdsAId: bankReceipt.tdsAId, bankReceiptTdsAName: bankReceipt.tdsAName,
                bankReceiptRoundOffAId: bankReceipt.roundOffAId, bankReceiptRoundOffAName: bankReceipt.roundOffAName,
                bankPaymentBankCommAId: bankPayment.bankCommAId, bankPaymentBankCommAName: bankPayment.bankCommAName,
                bankPaymentTdsAId: bankPayment.tdsAId, bankPaymentTdsAName: bankPayment.tdsAName,
                bankPaymentRoundOffAId: bankPayment.roundOffAId, bankPaymentRoundOffAName: bankPayment.roundOffAName,
                cashReceiptTdsAId: cashReceipt.tdsAId, cashReceiptTdsAName: cashReceipt.tdsAName,
                cashReceiptRoundOffAId: cashReceipt.roundOffAId, cashReceiptRoundOffAName: cashReceipt.roundOffAName,
                cashPaymentTdsAId: cashPayment.tdsAId, cashPaymentTdsAName: cashPayment.tdsAName,
                cashPaymentRoundOffAId: cashPayment.roundOffAId, cashPaymentRoundOffAName: cashPayment.roundOffAName,
            });
            setSnackbar({ open: true, message: 'Configuration saved successfully.', severity: 'success' });
            setConfirmOpen(false);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Save failed.', severity: 'error' });
        } finally { setSaveLoading(false); }
    };

    // ── Change handlers ────────────────────────────────────────────────────────

    const makeHandler = (setter) => (field, val) => {
        if (typeof val === 'object' && val !== null && val.id !== undefined)
            setter(prev => ({ ...prev, [`${field}Id`]: val.id, [`${field}Name`]: val.name }));
        else
            setter(prev => ({ ...prev, [field]: val }));
    };

    const onBankReceipt = makeHandler(setBankReceipt);
    const onBankPayment = makeHandler(setBankPayment);
    const onCashReceipt = makeHandler(setCashReceipt);
    const onCashPayment = makeHandler(setCashPayment);

    // ── Render ─────────────────────────────────────────────────────────────────

    if (loading) return <CenterLoader />;

    const shared = { connectionString, companyId, isMobile };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)`, background: 'linear-gradient(180deg, #F3F6FC 0%, #EEF2FA 100%)' }}>

            {/* ── Module header ──────────────────────────────────────────────── */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1877F2 0%, #0855A8 100%)',
                px: { xs: 1.5, md: 3 }, py: 1.3,
                boxShadow: '0 6px 16px rgba(24,119,242,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0,
            }}>
                {/* Left: title + breadcrumb */}
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <AccountBalanceIcon sx={{ fontSize: 15, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', md: '0.88rem' }, color: '#fff', letterSpacing: '0.1px' }}>
                            Bank / Cash A/c Configuration
                        </Typography>
                        <Chip label="FIN-BCP" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', borderRadius: '6px', flexShrink: 0 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.3, ml: 0.3 }}>
                        {['Settings', 'Financial Config', 'Bank / Cash Posting'].map((crumb, i, arr) => (
                            <Box key={crumb} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '0.66rem', color: i === arr.length - 1 ? '#fff' : 'rgba(255,255,255,0.6)', fontWeight: i === arr.length - 1 ? 700 : 400 }}>
                                    {crumb}
                                </Typography>
                                {i < arr.length - 1 && <NavigateNextIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mx: 0.2 }} />}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ── Scrollable body ────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', px: { xs: 1.5, md: 2 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>

                {/* Bank Transactions panel */}
                <GroupPanel
                    title="Bank Transactions"
                    subtitle="Ledger accounts for bank-based entries"
                    headerBg="linear-gradient(135deg, #1877F2 0%, #0855A8 100%)"
                    headerColor="#fff"
                    icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
                    rows={BANK_ROWS}
                    colA={bankReceipt} colB={bankPayment}
                    onChangeA={onBankReceipt} onChangeB={onBankPayment}
                    bookTypeA="BKR" bookTypeB="BKP"
                    {...shared}
                />

                {/* Cash Transactions panel */}
                <GroupPanel
                    title="Cash Transactions"
                    subtitle="Ledger accounts for cash-based entries"
                    headerBg="linear-gradient(135deg, #16A34A 0%, #0D7A37 100%)"
                    headerColor="#fff"
                    icon={<PaidIcon sx={{ fontSize: 16 }} />}
                    rows={CASH_ROWS}
                    colA={cashReceipt} colB={cashPayment}
                    onChangeA={onCashReceipt} onChangeB={onCashPayment}
                    bookTypeA="CBR" bookTypeB="CBP"
                    {...shared}
                />

                {/* Info strip */}
                <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', px: 1.75, py: 1, display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
                    <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: '#B45309', mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#78350F', lineHeight: 1.5 }}>
                        These accounts are used as default ledger entries when posting bank or cash transactions. Ensure the selected accounts are active and correctly categorised in your chart of accounts.
                    </Typography>
                </Box>
            </Box>

            {/* ── Footer action bar ──────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#0F172A', px: { xs: 1.5, md: 3 }, py: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', flexShrink: 0, gap: 1 }}>
                {!isMobile && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Module</Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.85)', fontWeight: 700, whiteSpace: 'nowrap' }}>Bank / Cash A/c Configuration</Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                    <Button variant="outlined" size="small" onClick={fetchData}
                        sx={{ height: 32, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', borderColor: 'rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.75)', borderRadius: '8px', px: 2, flex: { xs: 1, md: 'initial' }, '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.06)' } }}>
                        Refresh
                    </Button>
                    <Button variant="contained" size="small"
                        startIcon={saveLoading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <SaveOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                        onClick={() => setConfirmOpen(true)} disabled={saveLoading}
                        sx={{
                            height: 32, textTransform: 'none', fontWeight: 700, fontSize: '0.74rem',
                            background: 'linear-gradient(135deg, #1877F2 0%, #0855A8 100%)',
                            borderRadius: '8px', px: 2.2,
                            boxShadow: '0 6px 16px rgba(24,119,242,0.35)',
                            flex: { xs: 1, md: 'initial' }, whiteSpace: 'nowrap',
                            '&:hover': { boxShadow: '0 8px 18px rgba(24,119,242,0.45)' },
                        }}>
                        {saveLoading ? 'Saving…' : 'Save Configuration'}
                    </Button>
                </Box>
            </Box>

            {/* ── Snackbar ───────────────────────────────────────────────────── */}
            <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px', fontWeight: 700, boxShadow: '0 10px 28px rgba(15,23,42,0.2)' }}>{snackbar.message}</Alert>
            </Snackbar>

            {/* ── Confirm dialog ─────────────────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', border: '1px solid #EEF1F6', boxShadow: '0 20px 50px rgba(15,23,42,0.18)' } }}>
                <Box sx={{ background: 'linear-gradient(135deg, #1877F2 0%, #0855A8 100%)', px: 2.25, py: 1.4, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <WarningAmberOutlinedIcon sx={{ fontSize: 17, color: '#FBBF24' }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>Confirm Save</Typography>
                </Box>
                <Box sx={{ px: 2.25, py: 2.25 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.6 }}>
                        You are about to save the Bank / Cash Posting configuration for{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: '#1877F2' }}>
                            {selectedCompany?.CompanyName || 'this company'}
                        </Box>
                        . This will update all default ledger account mappings.
                    </Typography>
                </Box>
                <Divider sx={{ borderColor: '#EEF1F6' }} />
                <DialogActions sx={{ px: 2.25, py: 1.4, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" size="small"
                        sx={{ height: 32, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', borderRadius: '8px', borderColor: '#EEF1F6', color: '#475569', '&:hover': { borderColor: '#94a3b8', bgcolor: '#F7FAFF' } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" size="small" disabled={saveLoading}
                        sx={{
                            height: 32, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', borderRadius: '8px',
                            background: 'linear-gradient(135deg, #1877F2 0%, #0855A8 100%)',
                            boxShadow: '0 6px 16px rgba(24,119,242,0.3)',
                            '&:hover': { boxShadow: '0 8px 18px rgba(24,119,242,0.4)' },
                        }}>
                        {saveLoading ? 'Saving…' : 'Confirm & Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}